import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { isNestedArchiveFilePreviewKey } from 'js/file_viewer/core/data/preview_key';
import { useCurrentPreviewMetadata } from 'js/file_viewer/core/queries/preview_metadata';
import { prefetchAudioVideoRivieraData, useMediaMetadataQuery, useWaveformDataQuery, } from 'js/file_viewer/core/queries/riviera_preview_types/audio_video';
import { ErrorLifecycle, FVError, FVErrorCode } from 'js/file_viewer/core/types/errors';
import { PreviewType } from 'js/file_viewer/core/types/preview_type';
import { asyncComponent } from 'js/file_viewer/core/utils/suspense';
import { useDownloadSucceeded } from 'js/file_viewer/hooks/lifecycle_logging';
import { useRivieraTiming } from 'js/file_viewer/logging/hooks';
import { PreviewsEventLogger, PreviewsTimingEvents } from 'js/file_viewer/logging/performance';
const AsyncPreviewAudioVideo = asyncComponent('PreviewAudioVideo', () => import('js/file_viewer/preview_audio_video/preview_video'));
export const AsyncPreviewAudioVideoWithLifecycleLogging = (props) => {
    const { previewKey, previewSessionId, featureConfig, setLocalError } = props;
    React.useEffect(() => {
        if (isNestedArchiveFilePreviewKey(previewKey)) {
            setLocalError(new FVError(FVErrorCode.NestedFiletypeNotSupported, ErrorLifecycle.Render));
        }
    }, [previewKey, setLocalError]);
    const previewData = useCurrentPreviewMetadata();
    const queryClient = useQueryClient();
    React.useMemo(() => {
        if (previewData) {
            prefetchAudioVideoRivieraData(queryClient, previewKey, previewData, featureConfig.videojs_preload);
        }
    }, [featureConfig.videojs_preload, previewData, previewKey, queryClient]);
    React.useEffect(() => {
        PreviewsEventLogger.mark(PreviewsTimingEvents.ASYNC_VIDEO_CONTAINER_MOUNT);
    }, []);
    // We log both download_attempted and download_succeeded right away. Rendering the video preview does not require
    // loading the Riviera data. If we block on the Riviera data so we can log an accurate download_succeeded value, then
    // we regress page performance. If we let the PreviewAudioVideo render without first logging download_succeeded, then
    // our lifecycle events get out of order.
    useDownloadSucceeded(previewSessionId, true);
    const metadataResult = useMediaMetadataQuery(previewKey, previewData);
    const waveformResult = useWaveformDataQuery(previewKey, previewData);
    const lifecycleResult = (previewData === null || previewData === void 0 ? void 0 : previewData.previewType) === PreviewType.Audio ? waveformResult : metadataResult;
    useRivieraTiming(lifecycleResult, previewKey, previewData);
    return React.createElement(AsyncPreviewAudioVideo, { ...props });
};
AsyncPreviewAudioVideoWithLifecycleLogging.displayName = "AsyncPreviewAudioVideoWithLifecycleLogging"
//# sourceMappingURL=async_preview_video.preenvesnextjs.map
